import React, { useState, useEffect, useCallback } from 'react';
import { OWASP_OPTIONS } from '../constants/owasp';
import { useAuth } from '../context/AuthContext';
import { apiErrorMessage } from '../utils/apiError';
import { Card, Button, Badge, Input, Select, TextArea, Modal, Table } from '../components/Common';

const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Info'];

export default function VulnTemplates() {
  const { apiFetch, user, hasGroup, isMsp } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);

  // Client-side authors are the groups that already create findings. MSP staff
  // own the shared library; a client owns only templates carrying its client_id.
  const canAuthor = isMsp || hasGroup('Engagement Admins') || hasGroup('Penetration Testers');
  const isSharedTemplate = (row) => !row.client_id;
  const canModify = (row) => {
    if (!canAuthor) return false;
    if (isMsp) return true;
    return !!row.client_id && row.client_id === user?.clientId;
  };

  const load = useCallback(async () => {
    const res = await apiFetch('/vuln-templates');
    const data = await res.json().catch(() => null);
    setTemplates(Array.isArray(data) ? data : []);
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  // CSV import. MSP staff only, because it writes into the shared library.
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);   // { imported, skipped, errors }
  const [conflictReport, setConflictReport] = useState(null); // { conflicts, importable, file, errors }
  const fileInputRef = React.useRef(null);

  // One function for both passes. The first runs without ignoreConflicts and
  // the server answers 409 with the full conflict list, importing nothing; the
  // second runs with it and imports everything that does not collide.
  const sendImport = async (file, ignoreConflicts) => {
    const fd = new FormData();
    fd.append('file', file);
    if (ignoreConflicts) fd.append('ignoreConflicts', 'true');
    setImportBusy(true);
    try {
      const res = await apiFetch('/vuln-templates/import', { method: 'POST', body: fd, isFormData: true });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409 && data.code === 'TITLE_CONFLICTS') {
        setConflictReport({ ...data, file });
        return;
      }
      if (!res.ok) { alert(data.error || 'Import failed'); return; }
      setConflictReport(null);
      setImportResult(data);
      load();
    } finally {
      setImportBusy(false);
    }
  };

  const chooseImportFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';                       // so the same file can be re-picked
    if (!file) return;
    setImportResult(null);
    await sendImport(file, false);
  };

  const downloadImportTemplate = async () => {
    const res = await apiFetch('/vuln-templates/import/template');
    if (!res.ok) { alert('Could not download the sample file'); return; }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'vulnerability-templates-import.csv';
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  // Load a template into the edit dialog. Used by the Edit button and after a
  // clone, so the new copy is immediately editable rather than something the
  // user has to find again in the list.
  const openForEdit = (row) => {
    setEditing(row);
    setForm({
      title: row.title, cvssScore: row.cvss_score, cvssTags: row.cvss_tags,
      severity: row.severity, directExploitability: row.direct_exploitability,
      details: row.details, observation: row.observation,
      recommendation: row.recommendation, references: row.references, category: row.category,
      // Without these the boxes render empty on a populated record, which
      // reads as "the import dropped them".
      owaspCategory: row.owasp_category || '',
      stepsToReproduce: row.steps_to_reproduce || '',
      impact: row.impact || '',
      platforms: row.platforms || '',
      cwe: row.cwe || '',
    });
    setShowModal(true);
  };

  const save = async () => {
    const body = {
      title: form.title, cvssScore: parseFloat(form.cvssScore) || null, cvssTags: form.cvssTags,
      severity: form.severity, directExploitability: form.directExploitability,
      details: form.details, observation: form.observation, recommendation: form.recommendation, references: form.references, category: form.category,
      // Added when the fields were, which they should have been: the controls
      // existed and the values went nowhere. owaspCategory is the same gap and
      // predates this change -- that dropdown has never saved from this form.
      owaspCategory: form.owaspCategory ?? form.owasp_category ?? '',
      stepsToReproduce: form.stepsToReproduce ?? form.steps_to_reproduce ?? '',
      impact: form.impact ?? '',
      platforms: form.platforms ?? '',
      cwe: form.cwe ?? '',
    };
    const res = editing
      ? await apiFetch(`/vuln-templates/${editing.id}`, { method: 'PUT', body: JSON.stringify(body) })
      : await apiFetch('/vuln-templates', { method: 'POST', body: JSON.stringify(body) });
    if (!res.ok) {
      alert(await apiErrorMessage(res, 'Failed to save template'));
      return;
    }
    const saved = await res.json().catch(() => null);
    setShowModal(false); setForm({}); setEditing(null);
    await load();
    // A newly created template lands in the caller's own library, so it can be
    // edited and deleted straight away — show it rather than leaving the user
    // to hunt for it in a list sorted by category.
    if (saved && saved.id && !editing) setViewing(saved);
  };

  const clone = async (row) => {
    const res = await apiFetch(`/vuln-templates/${row.id}/clone`, { method: 'POST' });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      alert(d.error || 'Failed to clone template');
      return;
    }
    const copy = await res.json().catch(() => null);
    await load();
    // The copy belongs to the cloner, so it is theirs to edit and delete.
    // Open it directly — cloning is nearly always the first half of "clone and
    // then change it".
    if (copy && copy.id) openForEdit(copy);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await apiFetch(`/vuln-templates/${id}`, { method: 'DELETE' });
    load();
  };

  const columns = [
    { key: 'title', label: 'Title' },
    { key: 'severity', label: 'Severity', render: v => <Badge severity={v}>{v}</Badge> },
    { key: 'cvss_score', label: 'CVSS' },
    { key: 'category', label: 'Category', render: v => v || '—' },
    { key: 'client_id', label: 'Library', render: (v, row) => isSharedTemplate(row) ? (
      <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 6px', background: 'var(--bg-input)', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: 8 }}>SHARED</span>
    ) : (
      <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 6px', background: '#22c55e', color: '#fff', borderRadius: 8 }}>
        {isMsp ? (row.client_name || 'CLIENT') : 'OURS'}
      </span>
    )},
    { key: 'id', label: 'Actions', render: (v, row) => (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {/* Everyone who can see a template can read it — a client needs to know
            what a shared template contains before deciding to clone it. */}
        <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
          onClick={(e) => { e.stopPropagation(); setViewing(row); }}>View</Button>
        {canModify(row) && (
          <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); openForEdit(row); }}>Edit</Button>
        )}
        {canAuthor && (
          <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
            title={isSharedTemplate(row) ? 'Copy into your own library, where you can edit it' : 'Duplicate in your library'}
            onClick={(e) => { e.stopPropagation(); clone(row); }}>Clone</Button>
        )}
        {canModify(row) && (
          <Button variant="danger" style={{ padding: '4px 12px', fontSize: 12 }} onClick={(e) => { e.stopPropagation(); del(v); }}>Delete</Button>
        )}
      </div>
    )}
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Vulnerability Templates</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Importing writes to the shared library, so it is MSP staff only. */}
          {isMsp && (
            <>
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
                onChange={chooseImportFile} />
              <Button variant="secondary" disabled={importBusy}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                {importBusy ? 'Importing...' : 'Import CSV'}
              </Button>
              <Button variant="secondary" onClick={downloadImportTemplate}>Sample CSV</Button>
            </>
          )}
          {canAuthor && <Button onClick={() => { setEditing(null); setForm({}); setShowModal(true); }}>+ New Template</Button>}
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <Input placeholder="Search templates..." value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 0, maxWidth: 400 }} />
      </div>
      <Table columns={columns} data={templates.filter(t => {
        if (!search) return true;
        const q = search.toLowerCase();
        return [t.title, t.severity, t.category, t.cvss_score].filter(Boolean).join(' ').toLowerCase().includes(q);
      })} />
      {/* Conflicts. Nothing has been imported at this point -- the server
          reports and stops -- so this is a decision, not a notification. Every
          clashing title is listed rather than counted, because "12 conflicts"
          does not tell anyone whether the right thing will happen. */}
      <Modal isOpen={!!conflictReport} onClose={() => setConflictReport(null)}
        title="Some templates already exist" width={680}>
        {conflictReport && (
          <>
            <div style={{ fontSize: 14, marginBottom: 12 }}>
              {conflictReport.conflicts.length} of the rows in this file
              {conflictReport.conflicts.length === 1 ? ' matches a template' : ' match templates'} already in the
              library. Nothing has been imported yet.
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--bg-input)' }}>
                    <th style={{ textAlign: 'left', padding: '6px 10px', width: 60 }}>Row</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px' }}>Title</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px' }}>Conflict</th>
                  </tr>
                </thead>
                <tbody>
                  {conflictReport.conflicts.map((c, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '6px 10px', color: 'var(--text-muted)' }}>{c.row}</td>
                      <td style={{ padding: '6px 10px' }}>{c.title}</td>
                      <td style={{ padding: '6px 10px', color: 'var(--text-secondary)' }}>{c.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {conflictReport.errors && conflictReport.errors.length > 0 && (
              <div style={{ fontSize: 12, color: '#f59e0b', marginBottom: 10 }}>
                {conflictReport.errors.length} row(s) could not be read and will be skipped either way:{' '}
                {conflictReport.errors.slice(0, 3).map(e => `row ${e.row} (${e.error})`).join('; ')}
                {conflictReport.errors.length > 3 ? ', ...' : ''}
              </div>
            )}
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Continuing imports the {conflictReport.importable} template(s) that do not conflict and skips the
              ones listed above. Existing templates are never overwritten.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setConflictReport(null)}>Cancel</Button>
              <Button disabled={importBusy || conflictReport.importable === 0}
                onClick={() => sendImport(conflictReport.file, true)}>
                {importBusy ? 'Importing...' : `Import ${conflictReport.importable}, skip the conflicts`}
              </Button>
            </div>
          </>
        )}
      </Modal>

      <Modal isOpen={!!importResult} onClose={() => setImportResult(null)} title="Import complete" width={560}>
        {importResult && (
          <>
            <div style={{ fontSize: 14, marginBottom: 10 }}>
              Imported <strong>{importResult.imported}</strong> template(s).
              {importResult.skipped > 0 && ` Skipped ${importResult.skipped} that already existed.`}
            </div>
            {importResult.errors && importResult.errors.length > 0 && (
              <div style={{ fontSize: 13, color: '#f59e0b', marginBottom: 10 }}>
                {importResult.errors.length} row(s) could not be read:
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {importResult.errors.slice(0, 10).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                </ul>
              </div>
            )}
            {importResult.unknownColumns && importResult.unknownColumns.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                Ignored unrecognised column(s): {importResult.unknownColumns.join(', ')}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => setImportResult(null)}>Close</Button>
            </div>
          </>
        )}
      </Modal>

      <Modal isOpen={!!viewing} onClose={() => setViewing(null)} title={viewing?.title || 'Template'} width={800}>
        {viewing && (
          <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
              <Badge severity={viewing.severity}>{viewing.severity}</Badge>
              {viewing.cvss_score != null && (
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>CVSS {viewing.cvss_score}</span>
              )}
              {viewing.category && (
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>· {viewing.category}</span>
              )}
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 8,
                ...(isSharedTemplate(viewing)
                  ? { background: 'var(--bg-input)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }
                  : { background: '#22c55e', color: '#fff' })
              }}>
                {isSharedTemplate(viewing) ? 'SHARED' : (isMsp ? (viewing.client_name || 'CLIENT') : 'OURS')}
              </span>
            </div>
            {viewing.cvss_tags && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>{viewing.cvss_tags}</div>
            )}
            {[['Direct Exploitability', viewing.direct_exploitability], ['Details', viewing.details],
              ['Observation', viewing.observation], ['Recommendation', viewing.recommendation],
              ['References', viewing.references]].map(([label, value]) => value ? (
              <div key={label} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--text-secondary)' }}>{label}</div>
                <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{value}</div>
              </div>
            ) : null)}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant="secondary" onClick={() => setViewing(null)}>Close</Button>
              {canAuthor && isSharedTemplate(viewing) && (
                <Button onClick={() => { const t = viewing; setViewing(null); clone(t); }}>Clone to my library</Button>
              )}
              {canModify(viewing) && (
                <Button onClick={() => { const t = viewing; setViewing(null); openForEdit(t); }}>Edit</Button>
              )}
              {canModify(viewing) && (
                <Button variant="danger" onClick={() => { const t = viewing; setViewing(null); del(t.id); }}>Delete</Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Template' : 'New Template'} width={800} noOverlayClose>
        <Input label="Title *" value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Select label="Severity *" options={[{ value: '', label: 'Select' }, ...SEVERITIES.map(s => ({ value: s, label: s }))]}
            value={form.severity || ''} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))} />
          <Input label="CVSS Score" type="number" min="0" max="10" step="0.1" value={form.cvssScore || ''} onChange={e => setForm(f => ({ ...f, cvssScore: e.target.value }))} />
          {/* Two taxonomies, so they are labelled as two. "Library group" is
              the free-text grouping this library has always had; OWASP is the
              Top 10 code. Both called "Category" is what would read as a
              duplicate and let one of them rot. */}
          <Input label="Library group" value={form.category || ''} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
          <Select label="OWASP Top 10:2021" options={OWASP_OPTIONS}
            value={form.owaspCategory ?? form.owasp_category ?? ''}
            onChange={e => setForm(f => ({ ...f, owaspCategory: e.target.value }))} />
        </div>
        <Input label="CVSS Tags" value={form.cvssTags || ''} onChange={e => setForm(f => ({ ...f, cvssTags: e.target.value }))} />
        {/* Yes or No, matching what a finding records. This was a free text
            box, which is how templates came to hold Proven and Theoretical
            while findings held Yes and No -- and why a template marked Proven
            produced a finding marked No. */}
        <Select label="Direct Exploitability"
          options={[{ value: 'No', label: 'No' }, { value: 'Yes', label: 'Yes' }]}
          value={form.directExploitability === 'Yes' ? 'Yes' : 'No'}
          onChange={e => setForm(f => ({ ...f, directExploitability: e.target.value }))} />
        <TextArea label="Details" value={form.details || ''} onChange={e => setForm(f => ({ ...f, details: e.target.value }))} />
        <TextArea label="Observation" value={form.observation || ''} onChange={e => setForm(f => ({ ...f, observation: e.target.value }))} />
        {/* Carried onto the finding as its steps to reproduce, which is the one
            field here a finding already had a column for. */}
        <TextArea label="Proof of concept" value={form.stepsToReproduce ?? form.steps_to_reproduce ?? ''}
          onChange={e => setForm(f => ({ ...f, stepsToReproduce: e.target.value }))} />
        <TextArea label="Impact" value={form.impact || ''} onChange={e => setForm(f => ({ ...f, impact: e.target.value }))} />
        <TextArea label="Recommendation" value={form.recommendation || ''} onChange={e => setForm(f => ({ ...f, recommendation: e.target.value }))} />
        <TextArea label="References" value={form.references || ''} onChange={e => setForm(f => ({ ...f, references: e.target.value }))} />
        <div style={{ display: 'flex', gap: 12 }}>
          <Input label="Platforms" placeholder="Web, API, Mobile" value={form.platforms || ''}
            onChange={e => setForm(f => ({ ...f, platforms: e.target.value }))} />
          <Input label="CWE" placeholder="CWE-79, CWE-80" value={form.cwe || ''}
            onChange={e => setForm(f => ({ ...f, cwe: e.target.value }))} />
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={save} disabled={!form.title || !form.severity}>Save Template</Button>
        </div>
      </Modal>
    </div>
  );
}
